/**
 * @file time-picker/time-input.tsx
 *
 * TimeInput — text input mode for time entry.
 *
 * Layout:
 * ```
 *   ┌────────────────────────────────┐
 *   │ Enter time                     │  ← headline (labelMedium)
 *   │ [07] : [00] [AM]              │  ← input fields + PeriodSelector
 *   │              [PM]             │
 *   │  Hour   Minute                │  ← helper labels (bodySmall)
 *   └────────────────────────────────┘
 * ```
 *
 * Behavior:
 * - Two `<input>` fields for hour and minute
 * - Auto-advance: entering 2 valid digits automatically focuses next field
 * - Validation: error state when value is out of range
 * - AM/PM toggle on right (hidden when is24hour)
 *
 * Ported from `TimeInputImpl` in TimePicker.kt.
 *
 * @see docs/m3/date-and-time-pickers/time-pickers/TimePicker.kt (TimeInputImpl)
 */

import * as React from "react";
import { cn } from "../../../lib/utils";
import { PeriodSelector } from "./subcomponents/period-selector";
import { TimeField } from "./subcomponents/time-field";
import { TP_SIZE } from "./time-picker.tokens";
import type { TimeInputProps } from "./time-picker.types";

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * TimeInput — text input mode.
 *
 * Used with `useTimePickerState`:
 *
 * @example
 * ```tsx
 * const state = useTimePickerState({ initialHour: 9, initialMinute: 30 });
 *
 * // Standalone
 * <TimeInput state={state} />
 *
 * // In dialog (with toggle)
 * <TimePickerDialog
 *   open={open}
 *   onDismiss={() => setOpen(false)}
 *   confirmButton={<Button variant="text" onClick={() => setOpen(false)}>OK</Button>}
 * >
 *   <TimeInput state={state} />
 * </TimePickerDialog>
 * ```
 */
export const TimeInput: React.FC<TimeInputProps> = ({ state, className }) => {
	const { is24hour, setHourInput, setMinuteInput, setSelection } = state;

	const [focusedField, setFocusedField] = React.useState<
		"hour" | "minute" | null
	>(null);

	const handleHourAutoAdvance = (): void => {
		setFocusedField("minute");
		setSelection("minute");
	};

	return (
		<div
			className={cn(
				"bg-m3-surface-container-high rounded-m3-xl",
				"flex flex-col",
				"p-6 gap-0 select-none",
				className,
			)}
		>
			{/* ── Headline ── */}
			<p
				className="w-full text-left text-m3-on-surface-variant mb-5"
				style={{ fontSize: "12px", fontWeight: 500, letterSpacing: "0.1em" }}
			>
				Enter time
			</p>

			{/* ── Input Row: fields + separator + AM/PM ── */}
			<div className="flex flex-row items-start gap-2">
				{/* Hour input */}
				<div className="flex flex-col items-center gap-1">
					<TimeField
						mode="input"
						value={state.hourInput}
						field="hour"
						isFocused={focusedField === "hour"}
						isValid={state.isHourInputValid}
						onChange={setHourInput}
						onAutoAdvance={handleHourAutoAdvance}
						onFocus={() => {
							setFocusedField("hour");
							setSelection("hour");
						}}
						onBlur={() => setFocusedField(null)}
					/>
					<span className="text-[11px] text-m3-on-surface-variant font-normal leading-none mt-1">
						Hour
					</span>
				</div>

				{/* Separator ":" */}
				<span
					className="text-m3-on-surface text-center self-start"
					style={{
						fontSize: "57px",
						fontWeight: 400,
						lineHeight: 1,
						width: 24,
						// Aligned with height of input field
						marginTop: `${(TP_SIZE.timeFieldHeight - 57) / 2}px`,
					}}
					aria-hidden
				>
					:
				</span>

				{/* Minute input */}
				<div className="flex flex-col items-center gap-1">
					<TimeField
						mode="input"
						value={state.minuteInput}
						field="minute"
						isFocused={focusedField === "minute"}
						isValid={state.isMinuteInputValid}
						onChange={setMinuteInput}
						onFocus={() => {
							setFocusedField("minute");
							setSelection("minute");
						}}
						onBlur={() => setFocusedField(null)}
					/>
					{/* Helper label */}
					<span className="text-[11px] text-m3-on-surface-variant font-normal leading-none mt-1">
						Minute
					</span>
				</div>

				{/* AM/PM (hidden when is24hour) */}
				{!is24hour && (
					<PeriodSelector
						state={state}
						layout="vertical"
						className="ml-2 self-start"
						style={{
							height: TP_SIZE.periodInputHeight,
							width: TP_SIZE.periodInputWidth,
						}}
					/>
				)}
			</div>
		</div>
	);
};

TimeInput.displayName = "TimeInput";
